import React from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {Button, Heading, Link} from '@taopix/taopix-design-system';
import {
  AlternativeProductData,
  useChangeProductContext,
} from '../Context/ChangeProduct/ChangeProductContext';
import {useTaopix} from '../Context/TaopixContext';
import {PricingEngineInterface, ProductLayoutPricing} from '@taopix/taopix-core/dist/types/types';
import {useSelector} from 'react-redux';
import {Common, Lang, Localisation} from '@taopix/taopix-core';
import {PhotoBookPricingEngine} from '@taopix/taopix-core/dist/types/PricingEngine/PhotoBookPricingEngine';
import useSettings from '../../Hooks/useSettings';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {LayoutSelector, TotalPageCountSelector} from '../../Selectors';
import ProductPreviewDialog from './ChangeProductPreviewDialog';
import {ChangeProductActions} from '../Context/ChangeProduct/ChangeProductActionTypes';
import {getUrl} from '../../Utils/Common';

export type ProductPreviewPanelProps = {
  onChangeProduct: (ref: number) => void;
  showPreviewPanelAsDialog: boolean;
  selectedProduct: AlternativeProductData;
  currency: any;
  isChangeProduct: boolean;
};

const ProductPreviewPanel = ({
  onChangeProduct,
  showPreviewPanelAsDialog,
  selectedProduct,
  currency,
  isChangeProduct,
  ...props
}: ProductPreviewPanelProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();
  const settings = useSettings();

  const changeProduct = useChangeProductContext();
  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);

  const serverURL = window.config.baseURL;
  const thumbnailPath = settings.state.urls.thumbnailPath;

  const pricingEngine = app.core.get('pricingEngine');

  // get the imagePath depending on if we are in change product or upgrade and if highDPI or not
  let highDPIPreviewImage;
  let highDPIUpgradeLayoutPreviewImage;

  const getImagePath = (selectedProduct: AlternativeProductData) => {
    let previewImage;
    if (isChangeProduct) {
      previewImage = selectedProduct.previewImage;
      if (selectedProduct.highDPIPreviewImage) {
        highDPIPreviewImage = `${serverURL}/${thumbnailPath}/products/${productLayout.collectioncode}/${selectedProduct.thumbPath}/${selectedProduct.highDPIPreviewImage} 2x`;
      }
    } else {
      previewImage = selectedProduct.upgradeLayoutPreviewImage;
      if (selectedProduct.highDPIUpgradeLayoutPreviewImage) {
        highDPIUpgradeLayoutPreviewImage = `${serverURL}/${thumbnailPath}/products/${productLayout.collectioncode}/${selectedProduct.thumbPath}/${selectedProduct.highDPIUpgradeLayoutPreviewImage} 2x`;
      }
    }
    return `${serverURL}/${thumbnailPath}/products/${productLayout.collectioncode}/${selectedProduct.thumbPath}/${previewImage}`;
  };

  const getProjectPrice = (pricingModel?: ProductLayoutPricing, formatPrice?: boolean) => {
    const pricingEngineInstance = new pricingEngine(
      currency,
      1,
      pricingModel,
      settings.state.ai.mode
    );
    const projectPrice = pricingEngineInstance.calculateProjectPrice(pageCount, [], 0, 0);

    return formatPrice ? pricingEngine.formatCurrencyNumber(projectPrice, currency) : projectPrice;
  };

  const canShowImg = (product: AlternativeProductData) => {
    if (isChangeProduct) {
      return '' !== product.thumbPath && '' !== product.thumbnailImage;
    } else {
      return '' !== product.thumbPath && '' !== product.upgradeLayoutPreviewImage;
    }
  };

  // Check the upgradeLayoutPrompt and if it contains '^0' replace this with the price difference between the 2 products
  const getUpgradeLayoutPrompt = (upgradeLayoutPrompt: string) => {
    let newUpgradeLayoutPrompt = upgradeLayoutPrompt;
    if (upgradeLayoutPrompt.includes('^0')) {
      const newProjectPrice = getProjectPrice(selectedProduct?.pricing, false);
      const currentProjectPrice = getProjectPrice(productLayout?.pricing, false);

      const priceDiff = pricingEngine.formatCurrencyNumber(
        Number(newProjectPrice) - Number(currentProjectPrice),
        currency
      );

      newUpgradeLayoutPrompt = upgradeLayoutPrompt.replace('^0', priceDiff);
    }
    return Localisation.getLocaleString(Lang.getCode(), newUpgradeLayoutPrompt);
  };

  const productPreviewClasses = classNames(
    'w-full',
    'space-y-md',
    showPreviewPanelAsDialog ? 'mb-lg pt-lg px-lg overflow-auto' : ''
  );

  const productContentClasses = classNames('flex', 'lg:space-x-md', 'sm:flex-col', 'sm:space-y-md');

  const productDescriptionClasses = classNames(
    'flex-1',
    'space-y-md',
    'overflow-y-auto',
    'h-full',
    'max-w-[400rem]'
  );

  return (
    <>
      <div id={'product-preview-panel'} className={productPreviewClasses}>
        {!showPreviewPanelAsDialog && (
          <Heading level={ui.state.isSmallScreen ? 3 : 2}>{selectedProduct?.name}</Heading>
        )}
        <div className={productContentClasses}>
          {canShowImg(selectedProduct) && (
            <div className={'space-y-md w-[200rem]'}>
              <img
                alt={selectedProduct?.name}
                src={getImagePath(selectedProduct)}
                srcSet={isChangeProduct ? highDPIPreviewImage : highDPIUpgradeLayoutPreviewImage}
              ></img>
              {isChangeProduct && (
                <Button
                  data-test-id={'preview-image-button'}
                  label={t('*:str_ButtonPreview')}
                  size={'small'}
                  corners={'theme'}
                  buttonStyle={'distinct'}
                  onClick={() => {
                    changeProduct.dispatch(
                      ChangeProductActions.setChangeProductDialogPreviewImageDialogOpen(true)
                    );
                  }}
                />
              )}
            </div>
          )}
          <div className={productDescriptionClasses}>
            <p>
              {isChangeProduct
                ? selectedProduct?.description.replace(/^"(.+(?="$))"$/, '$1')
                : getUpgradeLayoutPrompt(productLayout?.upgradelayoutprompt)}
            </p>
            {settings.state.price.showPrice && (
              <p>
                {t('*:str_LabelFrom')}
                {': '}
                {getProjectPrice(selectedProduct?.pricing, true)}
              </p>
            )}
            {selectedProduct?.moreInformationURL !== '' && (
              <Link
                className={'underline'}
                href={getUrl(selectedProduct?.moreInformationURL)}
                target={'_blank'}
              >
                {t('*:str_ButtonMoreInformation')}
              </Link>
            )}
            <p>{t('optionsandextras:str_MessagePricesMayBeSubjectToTax')}</p>
            {settings.state.price.infoUrl !== '' && (
              <Link
                className={'underline'}
                href={getUrl(settings.state.price.infoUrl)}
                target={'_blank'}
              >
                {t('*:str_LabelMoreInfoShort')}
              </Link>
            )}
            {settings.state.price.disclaimer !== '' ? (
              <p>{Common.getCurrentLocaleString(settings.state.price.disclaimer, true)}</p>
            ) : (
              <p>{t('*:str_MessageTaxAndShipping')}</p>
            )}
          </div>
        </div>
      </div>
      <ProductPreviewDialog
        open={changeProduct.state.changeProductPreviewImageDialogOpen}
        onChangeProduct={onChangeProduct}
        selectedProduct={selectedProduct}
      />
    </>
  );
};

export default ProductPreviewPanel;
